import { ButtonHTMLAttributes, forwardRef } from 'react';

type Variant = 'primary' | 'default' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  block?: boolean;
}

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-brand text-white shadow-btn hover:bg-brand-dark',
  default: 'bg-white border border-line text-ink hover:bg-brand-shade hover:border-brand/40',
  ghost: 'bg-transparent text-ink-secondary hover:bg-brand-shade hover:text-brand',
  danger: 'bg-white border border-red-200 text-red-500 hover:bg-red-50',
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-[13px]',
  md: 'h-10 px-5 text-sm',
  lg: 'h-11 px-6 text-sm',
};

/** Custom button — replaces antd Button, driven by Chunaav design tokens. */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'default', size = 'md', block = false, className = '', type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      // eslint-disable-next-line react/button-has-type
      type={type}
      className={[
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        block ? 'w-full' : '',
        className,
      ].join(' ')}
      {...rest}
    />
  );
});

export default Button;
